import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTransactionFee } from 'appRedux/restApi/TransactionFee';
import { CURRENCIES, CURRENCY_MAP } from 'constants/app/currency-map';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import { INft, RestAxiosError } from 'interfaces';

export const useNftFormattedTotalPrice = (
  nft?: INft,
  method = 'mint',
  isMintConfirmed = false
) => {
  const { data: transactionFee, isLoading } = useQuery<
    Awaited<ReturnType<typeof getTransactionFee>>,
    RestAxiosError
  >({
    queryKey: [
      ReactQueryKeys.TRANSACTION_FEE,
      nft?.token_standard,
      method,
      nft?.token_id
    ],
    queryFn: () =>
      getTransactionFee({
        token_standard: nft?.token_standard,
        method,
        token_id: nft?.token_id,
        blockchain: nft?.blockchain
      }),
    enabled: !!nft && !isMintConfirmed
  });

  const VAT = useMemo(() => {
    if (!nft) {
      return 0;
    }
    const { fractionalDigits } = CURRENCIES[nft.currency] || CURRENCIES.FTN;
    return (nft.tax_inclusive_price - Number(nft.price)).toLocaleString(
      undefined,
      {
        maximumFractionDigits: fractionalDigits
      }
    );
  }, [nft]);

  const currency = useMemo(() => {
    if (!nft) {
      return CURRENCY_MAP.FTN;
    }
    return CURRENCY_MAP[nft.currency] || CURRENCY_MAP.FTN;
  }, [nft]);

  const nftFormattedTotalPrice = useMemo(() => {
    const isFeeOrNftAbsent = transactionFee ? !transactionFee || !nft : !nft;
    if (isFeeOrNftAbsent) {
      return '';
    }
    const totalPrice =
      (nft?.tax_inclusive_price || Number(nft?.price)) +
      (transactionFee && nft?.lazy_mint ? transactionFee?.data.data.price : 0);
    const { fractionalDigits } = CURRENCIES[nft?.currency] || CURRENCIES.FTN;

    return (
      totalPrice.toLocaleString(undefined, {
        maximumFractionDigits: fractionalDigits
      }) +
      ' ' +
      `${currency} `
    );
  }, [nft, transactionFee, currency]);

  if (!nft) {
    return {
      transactionFeeLoading: false,
      transactionFeeLoaded: true,
      transactionFee: {
        data: {
          data: {
            price: 0,
            formatted_price: '0'
          }
        }
      },
      nftFormattedTotalPrice: '',
      currency: CURRENCY_MAP.FTN,
      VAT: 0
    };
  }

  return {
    transactionFeeLoading: isMintConfirmed ? false : isLoading,
    transactionFeeLoaded: !!transactionFee,
    transactionFee,
    nftFormattedTotalPrice,
    currency,
    VAT
  };
};
